import React from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AxiosError } from "axios";
import { isAmplify, isLocalhost, isDevEnv } from "../utils";
import useLogger from "../Logger/useLogger";

/**
 * Configure QueryClient with default options aligned with our application's
 *  Apollo Client patterns
 *
 * Key features:
 * - Smart retry logic (no retries on 4xx errors)
 * - Optional error logging (avoid duplicates with Axios interceptor)
 * - Aligned with Apollo Client and community best practices
 *
 * Note on Error Handling:
 * - 401/403 handling (logout/stopImpersonation): Done by Axios interceptor
 * - Error logging: Done by Axios interceptor (to avoid duplicates)
 * - TanStack Query errors: Only logged if NOT from Axios (e.g., queryFn errors)
 */
const createQueryClient = (logger: ReturnType<typeof useLogger>) => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: How long data is considered fresh
        staleTime: 5 * 60 * 1000, // 5 minutes

        // GC time: How long unused data stays in cache
        gcTime: 10 * 60 * 1000, // 10 minutes

        // Retry strategy matching REST best practices
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors (client errors)
          if (error instanceof AxiosError) {
            const status = error.response?.status;
            if (status && status >= 400 && status < 500) {
              return false; // Client errors shouldn't be retried
            }
          }
          // Retry up to 3 times for 5xx/network errors
          return failureCount < 3;
        },

        // Refetch behavior
        refetchOnWindowFocus: false, // Won't refetch when user returns to tab
        refetchOnReconnect: true, // Refetch when network reconnects
        refetchOnMount: true, // Refetch when component mounts
      },
      mutations: {
        // Mutations shouldn't retry by default
        retry: false,
      },
    },

    // Global error handler for queries
    // Note: Axios errors are already logged by the interceptor, so we skip them here
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Skip logging in dev/localhost/amplify
        if (isAmplify || isLocalhost || isDevEnv) {
          return;
        }

        // Skip if this is an AxiosError (already logged by Axios interceptor)
        if (error instanceof AxiosError) {
          // Axios interceptor already handled:
          // - 401/403: logout/stopImpersonation
          // - Logging: logWarning + logError
          // So we skip duplicate logging here
          return;
        }

        // Log non-Axios errors (e.g., errors thrown in queryFn before Axios)
        logger.logError(error, `TanStack Query Error: ${error.message}`, {
          tags: {
            queryKey: JSON.stringify(query.queryKey),
            type: "query",
          },
          extra: {
            error: error.message,
            queryKey: query.queryKey,
            queryHash: query.queryHash,
          },
        });
      },
    }),

    // Global error handler for mutations
    // Note: Axios errors are already logged by the interceptor, so we skip them here
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Skip logging in dev/localhost/amplify
        if (isAmplify || isLocalhost || isDevEnv) {
          return;
        }

        // Skip if this is an AxiosError (already logged by Axios interceptor)
        if (error instanceof AxiosError) {
          // Axios interceptor already handled:
          // - 401/403: logout/stopImpersonation
          // - Logging: logWarning + logError
          // So we skip duplicate logging here
          return;
        }

        // Log non-Axios errors (e.g., errors thrown in mutationFn before Axios)
        logger.logError(error, `TanStack Mutation Error: ${error.message}`, {
          tags: {
            mutationKey: JSON.stringify(mutation.options.mutationKey),
            type: "mutation",
          },
          extra: {
            error: error.message,
            mutationKey: mutation.options.mutationKey,
          },
        });
      },
    }),
  });
};

/**
 * QueryClientProvider wrapper with integrated logging
 *
 * Usage:
 * ```tsx
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 * ```
 *
 * Features:
 * - Automatic error logging to your Logger service
 * - React Query DevTools (development only)
 * - Aligned with Apollo Client error handling
 */
export const QueryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const logger = useLogger();

  // Create QueryClient once per provider instance
  const [queryClient] = React.useState(() => createQueryClient(logger));

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* DevTools - automatically hidden in production */}
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  );
};

export default QueryProvider;
